import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useFormik } from "formik";
import * as Yup from "yup";
import PageHeader from "./PageHeader";
import SurfaceCard from "./SurfaceCard";
import SafeImage from "components/SafeImage";
import { useFeedback } from "./FeedbackProvider";
import { IMAGE_TYPES, PHOTO_MAX_BYTES, compressPhoto, formatBytes } from "utils/uploads";

const PLACEHOLDER = "/Portrait_Placeholder.png";

// Results and Campaigns were two 413-line files doing the same thing to two
// different Firestore documents. Both also did `setData(res?.data)`, which
// puts `undefined` in state when the document has no images yet and then
// throws on `data.map`. One component, one empty state.
function MediaGallery({ title, description, itemNoun, fetchAll, addImage, removeImage }) {
  const { notify, confirm } = useFeedback();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(PLACEHOLDER);
  const [lightbox, setLightbox] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchAll();
      setImages(Array.isArray(result?.data) ? result.data : []);
    } catch (loadError) {
      console.error(`Could not load ${itemNoun}s:`, loadError);
      setError(`Could not load ${itemNoun}s.`);
    } finally {
      setLoading(false);
    }
  }, [fetchAll, itemNoun]);

  useEffect(() => {
    load();
  }, [load]);

  const validation = useFormik({
    initialValues: { photo: null },
    validationSchema: Yup.object().shape({
      photo: Yup.mixed()
        .required("Photo is required")
        .test(
          "fileSize",
          `Photo must be under ${formatBytes(PHOTO_MAX_BYTES)} after compression`,
          (value) => !value || !(value instanceof File) || value.size <= PHOTO_MAX_BYTES
        )
        .test("fileType", "Unsupported file format (only png, jpg, jpeg, webp)", (value) =>
          !value || !(value instanceof File) ? true : IMAGE_TYPES.includes(value.type)
        ),
    }),
    onSubmit: async (values) => {
      try {
        await addImage(values);
        handleClose();
        notify(`${itemNoun} added.`);
        load();
      } catch (submitError) {
        console.error(`Could not add ${itemNoun}:`, submitError);
        notify(`Could not add the ${itemNoun.toLowerCase()}.`, "error");
      }
    },
  });

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!IMAGE_TYPES.includes(file.type)) {
      validation.setFieldError("photo", "Unsupported format. Upload png, jpg, jpeg or webp.");
      return;
    }

    try {
      const processed = await compressPhoto(file);
      validation.setFieldValue("photo", processed);

      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(processed);
    } catch (compressError) {
      console.error("Error compressing image:", compressError);
      validation.setFieldError("photo", "Could not process that image. Please try another.");
    }
  };

  const handleClose = () => {
    setPreview(PLACEHOLDER);
    validation.resetForm();
    setOpen(false);
  };

  const handleDelete = async (imageURL) => {
    const confirmed = await confirm({
      title: `Delete this ${itemNoun.toLowerCase()}?`,
      message: "The image is removed from the public site and from storage. This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await removeImage(imageURL);
      notify(`${itemNoun} deleted.`);
      load();
    } catch (deleteError) {
      console.error(`Could not delete ${itemNoun}:`, deleteError);
      notify(`Could not delete the ${itemNoun.toLowerCase()}.`, "error");
    }
  };

  return (
    <>
      <PageHeader
        title={title}
        description={description}
        actions={
          <>
            <Button
              onClick={load}
              startIcon={<RefreshIcon />}
              variant="outlined"
              sx={{ textTransform: "none" }}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", color: "#fff" }}
              onClick={() => setOpen(true)}
            >
              Add {itemNoun.toLowerCase()}
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <SurfaceCard>
        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : images.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 8 }}>
            <Typography sx={{ color: "#7b809a" }}>
              No {itemNoun.toLowerCase()}s published yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", color: "#fff" }}
              onClick={() => setOpen(true)}
            >
              Add the first one
            </Button>
          </Stack>
        ) : (
          <Grid container spacing={2}>
            {images.map((imageURL) => (
              <Grid item xs={6} sm={4} md={3} key={imageURL}>
                <Card
                  variant="outlined"
                  sx={{ position: "relative", borderRadius: 2, border: "1px solid #e3e8f0" }}
                >
                  <SafeImage src={imageURL} alt={itemNoun} onClick={() => setLightbox(imageURL)} />
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(imageURL)}
                    aria-label={`Delete ${itemNoun.toLowerCase()}`}
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      bgcolor: "rgba(255,255,255,0.92)",
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    <DeleteForeverIcon fontSize="small" sx={{ color: "#f44335" }} />
                  </IconButton>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </SurfaceCard>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          Add {itemNoun.toLowerCase()}
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", right: 12, top: 12 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={validation.handleSubmit} noValidate>
          <DialogContent dividers>
            <Box
              component="img"
              src={preview}
              alt="Preview"
              sx={{
                width: "100%",
                maxHeight: 280,
                objectFit: "contain",
                borderRadius: 2,
                border: "1px solid #e3e8f0",
                mb: 2,
                bgcolor: "#f4f6fa",
              }}
            />
            <Typography sx={{ fontSize: "0.78rem", color: "#7b809a", mb: 0.5 }}>
              png, jpg, jpeg or webp. Large images are compressed to {formatBytes(PHOTO_MAX_BYTES)}{" "}
              automatically.
            </Typography>
            <input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleImageChange} />
            {validation.touched.photo && validation.errors.photo ? (
              <FormHelperText sx={{ color: "#f44335" }}>{validation.errors.photo}</FormHelperText>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ color: "#fff" }}
              disabled={validation.isSubmitting}
            >
              {validation.isSubmitting ? "Uploading…" : "Add"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox("")} maxWidth="md">
        <Box
          component="img"
          src={lightbox}
          alt={itemNoun}
          sx={{ maxWidth: "100%", maxHeight: "85vh", display: "block" }}
        />
      </Dialog>
    </>
  );
}

MediaGallery.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  itemNoun: PropTypes.string.isRequired,
  fetchAll: PropTypes.func.isRequired,
  addImage: PropTypes.func.isRequired,
  removeImage: PropTypes.func.isRequired,
};

export default MediaGallery;
